// case.ts — Moderation case types (CaseRecord, Vote, Decision, Appeal, CouncilTeam)
// README fidelity: FIVE AND ONLY FIVE moderation categories.

// The five and only five moderation categories (exact keys from legacy cases.js).
export type ModerationCategory =
  | 'threats'
  | 'terrorism'
  | 'scams'
  | 'fraud'
  | 'coordinated-abuse';

// Constant tuple of all five category ids — useful for iteration and validation.
export const MODERATION_CATEGORIES = [
  'threats',
  'terrorism',
  'scams',
  'fraud',
  'coordinated-abuse',
] as const satisfies ModerationCategory[];

// Human-readable labels for each category (mirrors legacy CATEGORY_LABELS).
export const CATEGORY_LABELS: Record<ModerationCategory, string> = {
  'threats': 'Direct Threats',
  'terrorism': 'Terrorism',
  'scams': 'Scams',
  'fraud': 'Fraud',
  'coordinated-abuse': 'Coordinated Abuse',
};

// Vote tally on a case.
export interface VoteTally {
  keep: number;
  remove: number;
}

// Vote direction.
export type VoteSide = 'keep' | 'remove';

// A decision record (set when a case reaches the threshold).
export interface Decision {
  outcome: 'kept' | 'removed';
  explanation: string;
  /** Empty for an expired case: no team ruled, so none is named. */
  team: string;
  /**
   * HOW the case ended — a different question from `outcome`. Both 'vote' and
   * 'expired' can read 'kept', and only one of them is a judgement. Optional
   * because a server older than 1.24.80 does not send it; absent means 'vote',
   * which is what every decision before then was.
   */
  resolution?: 'vote' | 'expired';
}

// An appeal record (set when a decided case is appealed and re-voted).
export interface Appeal {
  outcome: 'upheld' | 'overturned';
  explanation: string;
}

// Report weight (trust-score-derived — display only, never gates speech).
export type ReportWeight = 'Low' | 'Medium' | 'High';

// Case status values through the full state machine.
// 'awaiting-head' — the team is drawn but has no head moderator yet, so it
// cannot vote (council-governance spec §2.5). It is a PRE-review state, not a
// hidden one: the case is as public as 'under-review' and both clients show the
// member-facing "Under Review" label for it. Members never see this key.
export type CaseStatus =
  | 'awaiting-head'
  | 'under-review'
  | 'decided'
  | 'appeal-review'
  | 'appeal-resolved';

// A case record targets either a post or an account.
// Post cases carry `postId`; account cases carry `accountId`.
export type CaseKind = 'post' | 'account';

// A moderation case (the core state-machine record).
export interface CaseRecord {
  id: string;
  kind: CaseKind;
  // One of these will be set depending on `kind`
  postId?: string;
  accountId?: string;
  category: ModerationCategory;
  context: string;
  reporter: string;
  team: string;
  time: string;
  reportWeight: ReportWeight;
  status: CaseStatus;
  votes: VoteTally;
  decision: Decision | null;
  appeal: Appeal | null;
  // Per-viewer: the signed-in user's own vote on this case (or null). Drives the
  // Voting Board's "you voted" state under realistic one-vote-per-user (B5).
  myVote?: VoteSide | null;
}

// Council team member.
export interface TeamMember {
  name: string;
  av: string;
}

// A rotating council team.
export interface CouncilTeam {
  id: string;
  name: string;
  status: string;
  caseload: number;
  members: TeamMember[];
}
